import type { AgentChat } from '@/containers/agents/use-agent-chats.hook';
import { isRenamed, type RenamedMark } from '@/hooks/chat-titles.store';
import type { LocalChats, PendingChat } from '@/hooks/local-chats.util';
import type { ConversationSummary } from '@/hooks/use-agent-chat.hook';

/** The tabs show the most recent chats, this many of them. */
export const CHAT_TABS_LIMIT = 8;

/**
 * A chat as a tab: a Clu chat, or an agent's (`agentId` set). One the user
 * named (`renamed`) has its title win over the agent's name.
 */
export interface ChatTab extends RenamedMark {
  id: string;
  title: string;
  modifiedAt: string;
  agentId: string | null;
  /**
   * The row's React key when it differs from the id: the key a chat just
   * started was drawn under, kept once it has its id so the row stays put.
   */
  key?: string;
  /** Started, and waiting for its id: nothing to pin, close or open yet. */
  pending?: boolean;
}

/** A chat's row key: the key it was started under, else its id. */
export const chatRowKey = (chat: { id: string; key?: string }): string =>
  chat.key ?? chat.id;

const isoTime = (ms: number) => new Date(ms).toISOString();

/**
 * The lists with what the runtime knows before them: a chat the user sent
 * in moves as of the send; a chat started this session keeps the key it was
 * started under; and the chat just started, until a list holds it, joins
 * under its real id once known and its key until then. While it is the
 * chat the user sent in last it is the newest, whatever the server's clock
 * says; after a send elsewhere it sits at its own time. The result is
 * newest first.
 */
export function overlayLocalChats<
  T extends { id: string; modifiedAt: string; key?: string },
>(
  chats: readonly T[],
  local: LocalChats,
  pendingRow: (pending: PendingChat, modifiedAt: string) => T
): T[] {
  const { pending, sentAt, aliases } = local;
  const rows = chats.map((chat, index) => {
    const listedAt = chatTime(chat.modifiedAt);
    const time = Math.max(listedAt, sentAt[chat.id] ?? 0);
    const key = aliases[chat.id];
    if (key === undefined && time === listedAt) return { chat, index, time };
    const next: T = {
      ...chat,
      ...(key !== undefined ? { key } : {}),
      ...(time > listedAt ? { modifiedAt: isoTime(time) } : {}),
    };
    return { chat: next, index, time };
  });
  if (
    pending &&
    (pending.id === null || !chats.some((chat) => chat.id === pending.id))
  ) {
    const own = Math.max(
      pending.at,
      pending.id !== null ? (sentAt[pending.id] ?? 0) : 0
    );
    const sentElsewhere = Math.max(
      0,
      ...Object.entries(sentAt)
        .filter(([id]) => id !== pending.id)
        .map(([, time]) => time)
    );
    const newest = Math.max(0, ...rows.map((row) => row.time));
    const time = own >= sentElsewhere ? Math.max(own, newest + 1) : own;
    rows.push({ chat: pendingRow(pending, isoTime(time)), index: -1, time });
  }
  return rows
    .sort((a, b) => b.time - a.time || a.index - b.index)
    .map(({ chat }) => chat);
}

/** The tabs' chats with the runtime's own (see overlayLocalChats). */
export function withLocalChats(
  tabs: readonly ChatTab[],
  local: LocalChats
): ChatTab[] {
  return overlayLocalChats(tabs, local, (pending, modifiedAt) => ({
    id: pending.id ?? pending.key,
    key: pending.key,
    title: chatTabTitle(pending.title),
    modifiedAt,
    agentId: pending.agentId,
    ...(pending.id === null ? { pending: true } : {}),
  }));
}

/**
 * A chat's title as a tab. An untitled chat is not called New chat, the
 * name of the row above the tabs that starts one.
 */
export function chatTabTitle(title: string | null | undefined): string {
  return title?.trim() || 'Untitled chat';
}

/** At most this many agents have their chats read for the tabs. */
export const CHAT_TABS_AGENT_LIMIT = 8;

/**
 * The agents whose chats the tabs read: the live ones most recently
 * modified, at most `limit`, in one GET /agent/conversations?agent_ids=
 * (which takes at most 20).
 */
export function recentAgentIds(
  agents: readonly {
    id: string;
    modifiedAt: string;
    archivedAt: string | null;
  }[],
  limit = CHAT_TABS_AGENT_LIMIT
): string[] {
  return agents
    .filter((agent) => !agent.archivedAt)
    .map((agent) => ({ id: agent.id, time: chatTime(agent.modifiedAt) }))
    .sort((a, b) => b.time - a.time || a.id.localeCompare(b.id))
    .slice(0, limit)
    .map((agent) => agent.id);
}

/**
 * A server timestamp as milliseconds. The API may leave off the zone, which
 * `Date` would read as local time, so a bare timestamp is read as UTC.
 */
export function chatTime(value: string): number {
  const zoned = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;
  const time = Date.parse(zoned);
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Clu's chats and the agents' chats together, newest first. A chat in both
 * lists is the agent's.
 */
export function mergeChatTabs(
  cluChats: readonly (ConversationSummary & RenamedMark)[],
  agentChats: readonly (AgentChat & RenamedMark)[]
): ChatTab[] {
  const byId = new Map<string, ChatTab>();
  const add = (
    chat: ConversationSummary & RenamedMark,
    agentId: string | null
  ) =>
    byId.set(chat.id, {
      id: chat.id,
      title: chatTabTitle(chat.title),
      modifiedAt: chat.modified_at,
      agentId,
      ...(isRenamed(chat) ? { renamed: true } : {}),
      ...(chat.renamed_at ? { renamed_at: chat.renamed_at } : {}),
    });
  for (const chat of cluChats)
    add(chat, (chat as AgentChat).context_agent_id ?? null);
  for (const chat of agentChats) add(chat, chat.context_agent_id ?? null);
  return [...byId.values()].sort(
    (a, b) =>
      chatTime(b.modifiedAt) - chatTime(a.modifiedAt) ||
      a.id.localeCompare(b.id)
  );
}

/**
 * The tabs shown: the chats less the ones cleared (last moved at or before
 * `clearedBefore`), the ones closed (not moved since), and the ones pinned,
 * the most recent `limit` of them. A chat unpinned since (`reopened`, with
 * the time it came back) shows whatever Clear or Close said before, and
 * counts as moved when it came back, as Arc puts an unpinned tab on top.
 */
export function visibleChatTabs(
  chats: readonly ChatTab[],
  {
    clearedBefore = null,
    closed,
    pinned,
    reopened,
    limit = CHAT_TABS_LIMIT,
  }: {
    clearedBefore?: number | null;
    closed?: Readonly<Record<string, number>>;
    pinned?: ReadonlySet<string>;
    reopened?: Readonly<Record<string, number>>;
    limit?: number;
  } = {}
): ChatTab[] {
  const shownAt = (tab: ChatTab) =>
    Math.max(chatTime(tab.modifiedAt), reopened?.[tab.id] ?? 0);
  return chats
    .filter((tab) => {
      if (pinned?.has(tab.id)) return false;
      if (reopened?.[tab.id] !== undefined) return true;
      const time = chatTime(tab.modifiedAt);
      if (clearedBefore !== null && time <= clearedBefore) return false;
      const closedAt = closed?.[tab.id];
      return closedAt === undefined || time > closedAt;
    })
    .map((tab, index) => ({ tab, index, time: shownAt(tab) }))
    .sort((a, b) => b.time - a.time || a.index - b.index)
    .slice(0, limit)
    .map(({ tab }) => tab);
}

/**
 * The tabs: Clu's chats and the agents' chats together, newest first, less
 * the ones cleared (last moved at or before `clearedBefore`), the most recent
 * `limit` of them. A chat in both lists is the agent's.
 */
export function orderChatTabs(
  cluChats: readonly ConversationSummary[],
  agentChats: readonly AgentChat[],
  clearedBefore: number | null,
  limit = CHAT_TABS_LIMIT
): ChatTab[] {
  return visibleChatTabs(mergeChatTabs(cluChats, agentChats), {
    clearedBefore,
    limit,
  });
}

/**
 * An agent chat's tab reads as the agent, the way a browser tab reads as its
 * page: the agent's name, not the chat's first message. Clu chats, an agent
 * chat the user named, and one whose agent is not listed keep the chat's
 * title.
 */
export function titleAgentTabs(
  tabs: readonly ChatTab[],
  agentNames: ReadonlyMap<string, string>
): ChatTab[] {
  return tabs.map((tab) => {
    const name =
      tab.agentId && !tab.renamed
        ? agentNames.get(tab.agentId)?.trim()
        : undefined;
    return name ? { ...tab, title: name } : tab;
  });
}

/**
 * Where Clear puts the line: the newest of the tabs listed now, in the
 * server's own clock, so every chat listed hides and any chat that moves
 * afterwards comes back. Null with nothing to clear.
 */
export function clearCutoff(tabs: readonly ChatTab[]): number | null {
  if (tabs.length === 0) return null;
  return Math.max(...tabs.map((tab) => chatTime(tab.modifiedAt)));
}

const clearedBeforeKey = (userId: string) =>
  `cleanlist:sidebar-tabs:cleared-before:${userId}`;

/** The user's saved Clear line, or null (none, or storage unavailable). */
export function readClearedBefore(userId: string | null): number | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(clearedBeforeKey(userId));
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/** Saves the user's Clear line; without storage it lasts the session only. */
export function writeClearedBefore(userId: string, value: number): void {
  try {
    localStorage.setItem(clearedBeforeKey(userId), String(value));
  } catch {
    // Storage blocked or full: the caller keeps the line in memory.
  }
}

/** At most this many closed chats are remembered. */
const CLOSED_LIMIT = 50;

const closedKey = (userId: string) => `cleanlist:sidebar-tabs:closed:${userId}`;

/**
 * The chats the user closed, each with the time it last moved when closed,
 * saved beside the Clear line. Empty with none, or without storage.
 */
export function readClosedTabs(userId: string | null): Record<string, number> {
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(closedKey(userId));
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1])
      )
    );
  } catch {
    return {};
  }
}

/** Saves the closed chats; without storage they last the session only. */
export function writeClosedTabs(
  userId: string,
  closed: Readonly<Record<string, number>>
): void {
  try {
    localStorage.setItem(closedKey(userId), JSON.stringify(closed));
  } catch {
    // Storage blocked or full: the caller keeps them in memory.
  }
}

/**
 * The closed chats worth remembering: not the ones the Clear line hides
 * anyway, and only the most recent `limit`.
 */
export function pruneClosedTabs(
  closed: Readonly<Record<string, number>>,
  clearedBefore: number | null,
  limit = CLOSED_LIMIT
): Record<string, number> {
  return Object.fromEntries(
    Object.entries(closed)
      .filter(([, time]) => clearedBefore === null || time > clearedBefore)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
  );
}

/** At most this many unpinned chats are remembered as brought back. */
const REOPENED_LIMIT = 50;

const reopenedKey = (userId: string) =>
  `cleanlist:sidebar-tabs:reopened:${userId}`;

/**
 * The chats the user unpinned, each with the time it came back to the tabs,
 * saved beside the closed chats. Empty with none, or without storage.
 */
export function readReopenedTabs(
  userId: string | null
): Record<string, number> {
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(reopenedKey(userId));
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
      return {};
    return Object.fromEntries(
      Object.entries(parsed)
        .filter(
          (entry): entry is [string, number] =>
            typeof entry[1] === 'number' && Number.isFinite(entry[1])
        )
        .sort((a, b) => b[1] - a[1])
        .slice(0, REOPENED_LIMIT)
    );
  } catch {
    return {};
  }
}

/** Saves the unpinned chats; without storage they last the session only. */
export function writeReopenedTabs(
  userId: string,
  reopened: Readonly<Record<string, number>>
): void {
  try {
    localStorage.setItem(reopenedKey(userId), JSON.stringify(reopened));
  } catch {
    // Storage blocked or full: the caller keeps them in memory.
  }
}

/**
 * Brings unpinned chats back to the tabs: each shows again, first, as moved
 * `at`, whatever Clear or Close said, and only the most recent `limit` are
 * remembered.
 */
export function reopenChatTabs(
  ids: readonly string[],
  at: number,
  {
    reopened,
    closed,
  }: {
    reopened: Readonly<Record<string, number>>;
    closed: Readonly<Record<string, number>>;
  },
  limit = REOPENED_LIMIT
): { reopened: Record<string, number>; closed: Record<string, number> } {
  const back = new Set(ids);
  return {
    reopened: Object.fromEntries(
      Object.entries({
        ...reopened,
        ...Object.fromEntries(ids.map((id) => [id, at])),
      })
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
    ),
    closed: Object.fromEntries(
      Object.entries(closed).filter(([id]) => !back.has(id))
    ),
  };
}

/** The agent workspace's path, `/agents/<agentId>`, and its agent. */
export function workspaceAgentId(pathname: string): string | null {
  const match = /^\/agents\/([^/]+)\/?$/.exec(pathname);
  return match && match[1] !== 'plays' ? decodeURIComponent(match[1]) : null;
}

/**
 * The tab for the chat on screen, or null. An agent's chat is on screen in
 * its workspace, named by the URL's `chat`. A Clu chat is on screen as the
 * runtime's open chat, on /clu or in the dock.
 */
export function activeChatTabId({
  tabs,
  pathname,
  chatParam,
  runtimeConversationId,
  cluOnScreen,
  pendingId = null,
}: {
  tabs: readonly ChatTab[];
  pathname: string;
  chatParam: string | undefined;
  runtimeConversationId: string | null;
  /** /clu is open, or the dock shows Clu's own chat. */
  cluOnScreen: boolean;
  /**
   * The tab of the chat the runtime has open while it waits for its id:
   * on screen where the runtime's chat is, until it has one.
   */
  pendingId?: string | null;
}): string | null {
  const agentId = workspaceAgentId(pathname);
  if (agentId) {
    const target = chatParam ?? pendingId;
    const tab = tabs.find(
      (candidate) => candidate.id === target && candidate.agentId === agentId
    );
    return tab?.id ?? null;
  }
  const target = runtimeConversationId ?? pendingId;
  if (!cluOnScreen || !target) return null;
  const tab = tabs.find(
    (candidate) => candidate.id === target && candidate.agentId === null
  );
  return tab?.id ?? null;
}
